import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createPropertyRequest,
  getPropertyRequest,
  updatePropertyRequest,
} from 'api';
import { useParams } from 'react-router-dom';
import { useAuth } from 'context/AuthContext';
import type { AddPropertyValues, UpdatePropertyRequest } from 'types';
import { UserRole } from 'types';

function useProperty(id?: number) {
  const { propertyId: propertyIdFromParams } = useParams<{ propertyId: string }>();
  const { isAuthenticated, role, profile } = useAuth();
  const queryClient = useQueryClient();

  const propertyId = id !== undefined ? String(id) : propertyIdFromParams;
  const numericId = Number(propertyId);
  const isNumeric = !isNaN(numericId) && numericId > 0;

  const canAccessProperty = () => {
    if (!isAuthenticated) return false;
    if (role === UserRole.OWNER || role === UserRole.MANAGER) {
      return true;
    }
    if (role === UserRole.TENANT) {
      return true; // Backend will verify
    }
    return false;
  };

  const {
    data,
    error,
    isLoading,
    isFetching,
    isFetched,
    isError,
    isLoadingError,
    isSuccess,
    refetch,
  } = useQuery({
    queryKey: ['property', numericId, role],
    queryFn: () => getPropertyRequest(numericId),
    enabled: isNumeric && canAccessProperty(),
    retry: (failureCount, error: any) => {
      if (error?.status === 403 || error?.status === 401) {
        return false;
      }
      return failureCount < 3;
    },
  });

  const isOwnerOfProperty = data?.owner?.id === profile?.id;
  const hasAccessToProperty =
    role === UserRole.OWNER ||
    role === UserRole.MANAGER ||
    (role === UserRole.TENANT && isOwnerOfProperty);

  const addPropertyMutation = useMutation({
    mutationFn: (data: AddPropertyValues) => createPropertyRequest(data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['properties'] });
    },
  });

  const updatePropertyMutation = useMutation({
    mutationFn: (data: UpdatePropertyRequest) => updatePropertyRequest(data),
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ['property', variables.id, role],
      });
      await queryClient.invalidateQueries({ queryKey: ['properties'] });
    },
    onError: (error) => {
      console.error('Ошибка при обновлении недвижимости:', error);
    },
  });

  return {
    property: data,
    error,
    isLoading,
    isFetching,
    isFetched,
    isError,
    isLoadingError,
    isSuccess,
    refetch,
    propertyId: isNumeric ? numericId : 0,
    canAccessProperty: canAccessProperty(),
    isOwnerOfProperty,
    hasAccessToProperty,
    userRole: role,
    addPropertyMutation,
    updatePropertyMutation,
  };
}

export default useProperty;
